import { transactionIdSetter, parseTransactionIds } from "helpers/transactionIdFilter";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { FilterItemTypes } from "configs/types";
import { useAppSelector } from "store/index";

import Filter from "components/global/Filter/Filter";
import gridIcon from "assets/icons/grid-icon.svg";

interface Props {
  selectedCurrencies: { value: string | number; label: string }[];
  handleUpdateCurrencies: (
    e: { value: string | number; label: string }[],
  ) => void;

  selectedStatuses: { value: string; label: string }[];
  handleUpdateStatuses: (
    e: { value: string | number; label: string }[],
  ) => void;

  handleUpdateUserIds: (ids: number[]) => void;
  handleUpdatePaymentMethod: (val: string) => void;
  externalRef: string;
  setExternalRef: (value: string) => void;
  createdFrom: string;
  createdTo: string;
  setCreatedRange: (from: string, to: string) => void;
  completedFrom: string;
  completedTo: string;
  setCompletedRange: (from: string, to: string) => void;

  handleApply: () => void;
}

const WithdrawalsFilters: React.FC<Props> = ({
  selectedCurrencies,
  handleUpdateCurrencies,
  selectedStatuses,
  handleUpdateStatuses,
  handleUpdateUserIds,
  handleUpdatePaymentMethod,
  externalRef,
  setExternalRef,
  createdFrom,
  createdTo,
  setCreatedRange,
  completedFrom,
  completedTo,
  setCompletedRange,
  handleApply,
}) => {
  const { t } = useTranslation();

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const statusesFromStore = useAppSelector(
    (store) => store.paymentsWithdrawals.statusesList,
  );

  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [statuses, setStatuses] = useState<{ value: string; label: string }[]>([],);

  const [userIdInput, setUserIdInput] = useState("");
  const [paymentMethodInput, setPaymentMethodInput] = useState("");

  useEffect(() => {
    const result =
      currenciesList?.map((el) => ({
        value: el,
        label: el,
      })) ?? [];

    setCurrencies(result);
  }, [currenciesList]);

  useEffect(() => {
    const result = statusesFromStore?.map((el) => el) ?? [];
    setStatuses(result);
  }, [statusesFromStore]);

  const handleApplyCurrencies = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    handleUpdateCurrencies(isDelete ? e : [...selectedCurrencies, ...e]);
  };

const handleApplyStatuses = (
  e: { value: string | number; label: string }[], 
  isDelete?: boolean,
) => {
  handleUpdateStatuses(
    (isDelete ? e : [...selectedStatuses, ...e]).map((el) => ({
      value: String(el.value),
      label: el.label,
    })),
  );
};

  const handleApplyFilters = () => {
    const ids = parseTransactionIds(userIdInput);

    handleUpdateUserIds(ids);
    handleUpdatePaymentMethod(paymentMethodInput);

    handleApply();
  };

  const handleClearFilters = () => {
    setUserIdInput("");
    setPaymentMethodInput("");
    setExternalRef("");
    setCreatedRange("", "");
    setCompletedRange("", "");

    handleUpdateCurrencies([]);
    handleUpdateStatuses([]);
    handleUpdateUserIds([]);
    handleUpdatePaymentMethod("");

    handleApply();
  };

  return (
    <Filter
      handleClear={handleClearFilters}
      handleApply={handleApplyFilters}
      filterItems={[
        {
          type: FilterItemTypes.input,
          title: t("userId"),
          boxTitle: t("userId"),
          icon: gridIcon,
          inputValue: userIdInput,
          setInputValue: transactionIdSetter(setUserIdInput, true),
        },
        {
          type: FilterItemTypes.input,
          title: t("paymentMethod"),
          boxTitle: t("paymentMethod"),
          icon: gridIcon,
          inputValue: paymentMethodInput,
          setInputValue: setPaymentMethodInput,
        },
        {
          type: FilterItemTypes.input,
          title: "External ID",
          boxTitle: "External ID",
          icon: gridIcon,
          inputValue: externalRef,
          setInputValue: setExternalRef,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("currencies"),
          boxTitle: t("filterCurrencies"),
          icon: gridIcon,
          itemsArray: currencies,
          activeItems: selectedCurrencies,
          handleApply: handleApplyCurrencies,
        },
        {
          type: FilterItemTypes.multiselect,
          title: t("statuses"),
          boxTitle: t("filterStatuses"),
          icon: gridIcon,
          itemsArray: statuses,
          activeItems: selectedStatuses,
          handleApply: handleApplyStatuses,
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Created At",
          boxTitle: "Created At",
          icon: gridIcon,
          firstDatePlaceholder: "Created At From",
          secondDatePlaceholder: "Created At To",
          firstDateInitValue: createdFrom,
          secondDateInitValue: createdTo,
          handleApplyDate: (from, to) => setCreatedRange(from, to ?? ""),
          activeItems: [
            ...(createdFrom
              ? [{ value: "from", label: `From: ${createdFrom}` }]
              : []),
            ...(createdTo ? [{ value: "to", label: `To: ${createdTo}` }] : []),
          ],
          onDeleteItem: (value) =>
            setCreatedRange(
              value === "from" ? "" : createdFrom,
              value === "to" ? "" : createdTo,
            ),
        },
        {
          type: FilterItemTypes.datePicker,
          title: "Completed At",
          boxTitle: "Completed At",
          icon: gridIcon,
          firstDatePlaceholder: "Completed At From",
          secondDatePlaceholder: "Completed At To",
          firstDateInitValue: completedFrom,
          secondDateInitValue: completedTo,
          handleApplyDate: (from, to) => setCompletedRange(from, to ?? ""),
          activeItems: [
            ...(completedFrom
              ? [{ value: "from", label: `From: ${completedFrom}` }]
              : []),
            ...(completedTo
              ? [{ value: "to", label: `To: ${completedTo}` }]
              : []),
          ],
          onDeleteItem: (value) =>
            setCompletedRange(
              value === "from" ? "" : completedFrom,
              value === "to" ? "" : completedTo,
            ),
        },
      ]}
    />
  );
};

export const toIsoDateTime = (value: string) =>
  moment(value, "DD.MM.YYYY HH:mm", true).toISOString();

export default WithdrawalsFilters;
